/*
  <pack-choice> — "Just this pack / Both packs" in the PDP buy box
  ----------------------------------------------------------------
  Markup: snippets/pdp-pack-choice.liquid, rendered inside the product form above
  the quantity + Add to Cart row. Decision record: docs/XTRA_CARS_UPSELL_ROUND_BRIEF.md.

  The form posts one hidden `id`, the page's pack. While "Both" is checked this
  element rewrites `id` to the bundle's variant in the OUTGOING data only, on the
  browser's `formdata` event, which the page's own <product-form>
  (assets/product-form.js) fires when it builds `new FormData(this.form)`, and
  sets the line property `properties[_xsell_source]` from data-xsell-source
  (dropped again while "Just" is checked: the form carries no such field). So the
  add posts the bundle with no change to product-form.js, and the form itself is
  never touched: Shopify's pay-over-time line re-reads the form's `id` on every
  change event, Amazon's Buy with Prime widget and the wishlist heart read the pack
  too, and all of them keep seeing exactly what they see today.

  On every change of the radios it also:
    - relabels Add to Cart ("Add both packs" from data-both-label; the original
      label is read from the DOM, so the snippet carries no copy for it),
    - clears a stale add error (one written for the other choice),
    - publishes variantChange with the chosen unit price, which the perk meter
      already follows (assets/perk-meter.js). Its price must be a number.

  Buy with Prime is hidden by CSS alone while "Both" is checked
  (assets/component-pdp-pack-choice.css), so Amazon's late-injected widget is
  caught whenever it arrives and the checked radio is the single source of truth.

  Load order: after product-form.js and perk-meter.js (both earlier in the
  section), so the meter is subscribed when this connects. If "Both" is already
  checked on connect (a tap before this script ran), it is applied then.

  Back / Forward: the browser can restore the page from its back/forward cache
  exactly as it was left, label included. Safari then resets the radios (they
  carry autocomplete="off") to their HTML default, row 1, without a change
  event — which left row 1 checked under "Add both packs", and that add posted
  the single (found on iPhone 2026-10-19). So every such restore (`pageshow`
  with `persisted`) puts row 1 back here and re-applies: radio, label and meter
  agree in every browser, whichever of this and Safari's own reset runs first,
  since both land on row 1. The shopper re-picks the bundle row if they want it.

  Data attributes (set server-side by the snippet):
    data-section-id        — the section's id, carried on the published event
    data-pack-variant-id   — this pack's variant id
    data-pack-price        — this pack's price, in cents
    data-bundle-variant-id — the bundle's variant id
    data-bundle-price      — the bundle's price, in cents
    data-both-label        — Add to Cart's label while "Both" is checked
    data-xsell-source      — the bundle line's `_xsell_source` property
                             ("family-choice:<page handle>"), the January read
*/
if (!customElements.get('pack-choice')) {
  class PackChoice extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
      this.onFormData = this.onFormData.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
    }

    connectedCallback() {
      this.productForm = this.closest('product-form');
      this.form = this.closest('form');
      if (!this.productForm || !this.form) return;

      // Read once: on a reconnect while "Both" is checked, the label has already moved.
      if (this.packLabel === undefined) {
        const button = this.productForm.querySelector('[type="submit"]');
        this.label = button ? button.querySelector('span') : null;
        this.packLabel = this.label ? this.label.textContent : '';
      }

      this.addEventListener('change', this.onChange);
      this.form.addEventListener('formdata', this.onFormData);
      window.addEventListener('pageshow', this.onPageShow);
      if (this.isBoth()) this.apply();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      if (this.form) this.form.removeEventListener('formdata', this.onFormData);
      window.removeEventListener('pageshow', this.onPageShow);
    }

    isBoth() {
      const both = this.querySelector('input[type="radio"][value="both"]');
      return Boolean(both && both.checked);
    }

    onChange(event) {
      if (event.target.matches('input[type="radio"]')) this.apply();
    }

    // A back/forward-cache restore: back to row 1, then the label and the meter follow (see the header).
    onPageShow(event) {
      if (!event.persisted) return;
      const pack = this.querySelector('input[type="radio"][value="pack"]');
      if (pack) pack.checked = true;
      this.apply();
    }

    onFormData(event) {
      if (!this.isBoth()) return;
      event.formData.set('id', this.dataset.bundleVariantId);
      if (this.dataset.xsellSource) event.formData.set('properties[_xsell_source]', this.dataset.xsellSource);
    }

    apply() {
      const both = this.isBoth();
      if (this.label) this.label.textContent = both ? this.dataset.bothLabel : this.packLabel;
      if (typeof this.productForm.handleErrorMessage === 'function') this.productForm.handleErrorMessage();

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: this.dataset.sectionId,
            variant: {
              id: Number(both ? this.dataset.bundleVariantId : this.dataset.packVariantId),
              price: Number(both ? this.dataset.bundlePrice : this.dataset.packPrice),
            },
          },
        });
      }
    }
  }

  customElements.define('pack-choice', PackChoice);
}
